import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import { idSchema, isoDateSchema, reasonSchema, revisionSchema, text } from '../validators'

/**
 * Canonical issuer/security master (M9, P-CAN-001..005) and client issuer
 * links (P-CAN-004, SR-110..122). Feed-derived fields are gated by dataset
 * entitlements (P-CAN-003); investors see published versions only.
 */

export const canonicalIssuersQuerySchema = pageQuerySchema.extend({
  /** Name prefix/substring or an exact identifier value (LEI, ISIN, provider id). */
  q: text(0, 120).optional(),
  country: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
  sector: text(0, 60).optional(),
})
export type CanonicalIssuersQuery = z.infer<typeof canonicalIssuersQuerySchema>

export const canonicalIdentifierSchema = z.object({
  id: z.string(),
  type: z.string(),
  value: z.string(),
  source: z.string(),
  valid_from: z.string(),
  valid_to: z.string().nullable(),
  current: z.boolean(),
  /** Delivered by a feed provider: visible only with the dataset entitlement. */
  feed_derived: z.boolean(),
  dataset: z.string().nullable(),
})
export type CanonicalIdentifier = z.infer<typeof canonicalIdentifierSchema>

export const canonicalIssuerSummarySchema = z.object({
  id: z.string(),
  legal_name: z.string(),
  country: z.string(),
  sector: z.string(),
  status: z.string(),
  parent_issuer_id: z.string().nullable(),
  identifiers: z.array(z.object({ type: z.string(), value: z.string() })),
  /** How a search matched: by name or by a typed identifier. */
  matched_by: z.enum(['name', 'identifier']).nullable(),
})
export type CanonicalIssuerSummary = z.infer<typeof canonicalIssuerSummarySchema>

export const canonicalSecuritySchema = z.object({
  id: z.string(),
  identifier_type: z.string(),
  identifier_value: z.string(),
  name: z.string().nullable(),
  mapping_state: z.string(),
  effective_from: z.string(),
  effective_to: z.string().nullable(),
})
export type CanonicalSecurity = z.infer<typeof canonicalSecuritySchema>

export const researchVersionSchema = z.object({
  id: z.string(),
  canonical_id: z.string(),
  kind: z.string(),
  version: z.number().int(),
  dataset: z.string(),
  state: z.string(),
  is_latest: z.boolean(),
  provider_id: z.string().nullable(),
  provider_name: z.string().nullable(),
  provider_record_id: z.string().nullable(),
  feed_run_id: z.string().nullable(),
  version_hash: z.string(),
  retrieved_at: z.string().nullable(),
  rights_basis: z.string(),
  redistribution: z.string(),
  supersedes_id: z.string().nullable(),
  published_at: z.string().nullable(),
  created_at: z.string(),
  attribution: z.string().nullable(),
  data: z.object({
    title: z.string().nullable(),
    summary: z.string().nullable(),
    event_date: z.string().nullable(),
    publication_date: z.string().nullable(),
    source_url: z.string().nullable(),
  }),
})
export type ResearchVersion = z.infer<typeof researchVersionSchema>

export const issuerLinkSchema = z.object({
  id: z.string(),
  org_id: z.string(),
  org_name: z.string().nullable(),
  local_key: z.string(),
  local_name: z.string(),
  issuer_id: z.string(),
  issuer_name: z.string(),
  pinned_version_id: z.string().nullable(),
  reason: z.string(),
  linked_by: z.string(),
  linked_by_name: z.string().nullable(),
  linked_at: z.string(),
  unlinked_at: z.string().nullable(),
  unlinked_by: z.string().nullable(),
  unlink_reason: z.string().nullable(),
  revision: z.number().int(),
})
export type IssuerLink = z.infer<typeof issuerLinkSchema>

export const canonicalIssuerDetailSchema = z.object({
  id: z.string(),
  legal_name: z.string(),
  aliases: z.array(z.string()),
  country: z.string(),
  sector: z.string(),
  status: z.string(),
  description: z.string().nullable(),
  website: z.string().nullable(),
  provenance: z.string(),
  parent: z.object({ id: z.string(), legal_name: z.string() }).nullable(),
  subsidiaries: z.array(z.object({ id: z.string(), legal_name: z.string() })),
  identifiers: z.array(canonicalIdentifierSchema),
  securities: z.array(canonicalSecuritySchema),
  research_versions: z.array(researchVersionSchema),
  /** Datasets whose feed-derived fields the caller may see. */
  entitled_datasets: z.array(z.string()),
  /** Feed-derived items hidden for lack of an entitlement (counts only). */
  withheld: z.object({ identifiers: z.number().int(), research_versions: z.number().int() }),
  /** Staff only: client links to this issuer. Empty for investors. */
  links: z.array(issuerLinkSchema),
  attributions: z.array(
    z.object({ provider_id: z.string(), text: z.string(), terms_url: z.string() }),
  ),
})
export type CanonicalIssuerDetail = z.infer<typeof canonicalIssuerDetailSchema>

export const issuerLinksQuerySchema = pageQuerySchema.extend({
  org_id: idSchema.optional(),
  issuer_id: idSchema.optional(),
  include_closed: z.enum(['true', 'false']).optional(),
})
export type IssuerLinksQuery = z.infer<typeof issuerLinksQuerySchema>

/**
 * "Use issuer" (GAP-UI-020): `adopt` puts a canonical issuer under the client's
 * coverage and links it (next review required); `link` links an existing
 * client key. Identity is never confirmed by name similarity alone.
 */
export const issuerLinkCreateSchema = z
  .strictObject({
    org_id: idSchema,
    mode: z.enum(['adopt', 'link']),
    issuer_id: idSchema,
    local_key: text(1, 120),
    local_name: text(1, 300),
    reason: reasonSchema,
    next_review: isoDateSchema.optional(),
    scope: text(0, 500).optional(),
    pinned_version_id: idSchema.optional(),
  })
  .refine((v) => v.mode !== 'adopt' || v.next_review !== undefined, {
    message: 'Adopting an issuer needs a next-review date.',
    path: ['next_review'],
  })
export type IssuerLinkCreate = z.infer<typeof issuerLinkCreateSchema>

export const issuerLinkUnlinkSchema = z.strictObject({
  revision: revisionSchema,
  reason: reasonSchema,
})
export type IssuerLinkUnlink = z.infer<typeof issuerLinkUnlinkSchema>

export const issuerLinkRemapSchema = z.strictObject({
  revision: revisionSchema,
  issuer_id: idSchema,
  reason: reasonSchema,
})
export type IssuerLinkRemap = z.infer<typeof issuerLinkRemapSchema>
